import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Button, CircularProgress } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/WarningRounded';
import { useTranslation } from 'react-i18next';
import { subscribeUnsaved } from 'utils/unsavedChanges';

const useStyles = makeStyles(() => ({
    // Placed by TopNotices — at the top of the window, under what did not get in.
    root: {
        pointerEvents: 'auto',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
        maxWidth: 'calc(100vw - 32px)',
        padding: '8px 12px',
        borderRadius: 6,
        background: '#fff3e0',
        border: '1px solid #ffb74d',
        borderLeft: '5px solid #e65100',
        color: '#4e2600',
        fontSize: 14,
        fontWeight: 600,
        boxShadow: '0 3px 10px rgba(0,0,0,0.25)',
    },
    icon: { color: '#e65100' },
    save: { background: '#e65100', color: '#fff', '&:hover': { background: '#bf360c' } },
}));

/** A change shown only once it has stayed this long — an auto-save never flickers it. */
const SHOW_AFTER_MS = 600;

/**
 * «Є НЕЗБЕРЕЖЕНІ ЗМІНИ» — floating at the top of the window, wherever the
 * page is scrolled (organiser, 3 Nov 2026: «плашкою вгорі, яку буде видно на
 * любому місці сторінки, як плаваючий елемент»). Names each form with
 * changes not saved and offers its «Зберегти»; while there is one, the
 * browser asks before the page is closed or reloaded. Fed by
 * `useUnsavedChanges`; mounted once, in App.
 *
 * Stable hooks: unsaved-changes-bar, unsaved-changes-save.
 */
const UnsavedChangesBar = () => {
    const classes = useStyles();
    const [t] = useTranslation();
    const [entries, setEntries] = useState([]);
    const [shown, setShown] = useState(false);

    useEffect(() => subscribeUnsaved(setEntries), []);

    useEffect(() => {
        if (0 === entries.length) {
            setShown(false);
            return undefined;
        }
        const timer = setTimeout(() => setShown(true), SHOW_AFTER_MS);

        return () => clearTimeout(timer);
    }, [entries.length]);

    useEffect(() => {
        if (0 === entries.length) {
            return undefined;
        }
        const ask = event => {
            event.preventDefault();
            event.returnValue = ''; // eslint-disable-line no-param-reassign

            return '';
        };
        window.addEventListener('beforeunload', ask);

        return () => window.removeEventListener('beforeunload', ask);
    }, [entries.length]);

    if (!shown || 0 === entries.length) {
        return null;
    }

    return (
        <div className={classes.root} role='status' data-testid='unsaved-changes-bar'>
            <WarningIcon className={classes.icon} fontSize='small' />
            <span>
                {t('unsaved.title')}
                {': '}
                {entries.map(entry => `«${entry.label}»`).join(', ')}
            </span>
            {entries.filter(entry => entry.onSave).map(entry => (
                <Button
                    key={entry.id}
                    size='small'
                    variant='contained'
                    className={classes.save}
                    disabled={entry.saving}
                    startIcon={entry.saving ? <CircularProgress size={14} color='inherit' /> : null}
                    onClick={entry.onSave}
                    data-testid='unsaved-changes-save'
                >
                    {1 === entries.length ? t('unsaved.save') : t('unsaved.saveOne', { label: entry.label })}
                </Button>
            ))}
        </div>
    );
};

export default UnsavedChangesBar;
